import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Briefcase, Megaphone, HeadphonesIcon, BarChart3,
  Rocket, CalendarCheck, Settings, MessageCircle,
  ArrowRight, Sparkles, MessageSquareText, Cpu, Target,
  Layers, Puzzle, Zap, CheckCircle2, Clock,
  ChevronRight, Workflow, Globe, BadgeDollarSign,
  Plug, Calendar, Mail, CreditCard, FileText, Database
} from 'lucide-react';

/* ─── DATA ─── */

const examplePrompts = [
  'Get me more leads this month',
  'Follow up with all active prospects',
  'Run a commission report',
  'Build a marketing campaign',
];

const managesItems = [
  { icon: Briefcase, title: 'Sales & Lead Generation', description: 'Finds leads, creates outreach, and helps move deals forward.' },
  { icon: Megaphone, title: 'Marketing & Content', description: 'Builds campaigns, writes content, and supports promotion.' },
  { icon: HeadphonesIcon, title: 'Customer Support', description: 'Handles customer conversations with speed, care, and consistency.' },
  { icon: BarChart3, title: 'Data & Analytics', description: 'Turns business data into clear insights and better decisions.' },
  { icon: Rocket, title: 'Strategy & Growth', description: 'Helps identify opportunities and plan the next move.' },
  { icon: CalendarCheck, title: 'Executive Assistant', description: 'Keeps communication, scheduling, and daily workflow organized.' },
  { icon: Settings, title: 'Operations & Admin', description: 'Supports the systems and follow-through that keep business moving.' },
  { icon: MessageCircle, title: 'Social Media', description: 'Creates and organizes social content that drives engagement and growth.' },
];

const howSteps = [
  { num: '01', icon: MessageSquareText, title: 'You Ask', description: 'Tell Moxey what you need in plain language.' },
  { num: '02', icon: Cpu, title: 'Moxey Coordinates', description: 'Moxey interprets the request and activates the right systems behind the scenes.' },
  { num: '03', icon: Target, title: 'You Get Results', description: 'Instead of switching between tools, you get work completed faster and more clearly.' },
];

const modules = [
  {
    icon: Workflow,
    gradient: 'from-violet-600 to-purple-500',
    name: 'Moxey Flow',
    subtitle: 'CRM, Transactions, Commissions & E-Sign',
    description: 'Manage leads, contacts, transactions, commission tracking, reporting, direct deposit, and workflow from one place.',
  },
];

const thirdPartyIntegrations = [
  { icon: Calendar, label: 'Google Calendar' },
  { icon: Mail, label: 'Gmail / Email' },
  { icon: MessageCircle, label: 'Slack' },
  { icon: CreditCard, label: 'QuickBooks' },
  { icon: FileText, label: 'Google Docs' },
  { icon: Database, label: 'External CRM' },
];

const benefits = [
  { icon: Clock, title: 'Save time', description: 'Reduce hours spent on repetitive coordination and manual tasks.' },
  { icon: Puzzle, title: 'Reduce complexity', description: 'One AI replaces the need to juggle multiple tools and logins.' },
  { icon: CheckCircle2, title: 'Improve follow-through', description: 'Nothing falls through the cracks when Moxey is on it.' },
  { icon: Zap, title: 'Get more done', description: 'Move faster with AI handling the heavy lifting across departments.' },
  { icon: Settings, title: 'Stay organized', description: 'Stay on top of tasks, contacts, and deadlines without the chaos.' },
  { icon: Rocket, title: 'Make faster decisions', description: 'Get insights and summaries instantly instead of digging through data.' },
];

const differentiators = [
  { icon: Layers, title: 'One AI, many capabilities', description: "You don't need to remember different tools or workflows. Just talk to Moxey." },
  { icon: Plug, title: 'Native modules and third-party tools', description: 'Moxey works across native modules and your existing business software from one place.' },
  { icon: Cpu, title: 'One orchestration layer', description: 'Moxey coordinates across systems with shared memory and context, so you never have to switch tools.' },
  { icon: Target, title: 'Delivers results, not just responses', description: 'Moxey is designed to get work done across your entire business, not just chat.' },
];

/* ─── HELPERS ─── */

function SectionLabel({ children }) {
  return <p className="text-[#2563eb] font-semibold text-sm tracking-wide uppercase font-body mb-3">{children}</p>;
}

/* ─── PAGE ─── */

export default function MoxeyHome() {
  return (
    <div data-testid="moxey-home-page">

      {/* ━━━ 1. HERO ━━━ */}
      <section className="bg-[#f8f9fb] pt-16 pb-20 lg:pt-24 lg:pb-28 px-6" data-testid="home-hero">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left */}
            <div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-900 tracking-tight leading-[1.1]"
                data-testid="home-hero-title"
              >
                Run your business.{' '}
                <span className="font-script text-[#2563eb]">Moxey</span> does the work.
              </h1>
              <p className="text-[#2563eb] mt-4 text-lg font-semibold font-body" data-testid="home-hero-subtitle">
                Meet Moxey, your AI Business Leader.
              </p>
              <p className="text-gray-500 mt-4 text-base sm:text-lg leading-relaxed font-body max-w-lg">
                Moxey works across your business — not just inside one app. It coordinates native modules and third-party tools behind the scenes so you can focus on results.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8" data-testid="home-hero-cta">
                <Link to="/hire-moxey">
                  <Button
                    className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-2xl px-8 py-6 text-base font-bold shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all"
                    data-testid="home-hire-btn"
                  >
                    Hire Moxey
                    <img src="/images/moxey-avatar.png" alt="Moxey" className="ml-2 w-5 h-5 rounded-full object-cover" />
                  </Button>
                </Link>
                <Link to="/moxey">
                  <Button
                    variant="outline"
                    className="rounded-2xl px-8 py-6 text-base font-semibold text-gray-700 border-gray-200 hover:bg-gray-50 transition-colors"
                    data-testid="home-learn-btn"
                  >
                    Learn More
                    <ChevronRight className="ml-1 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right – Moxey image */}
            <div className="flex justify-center lg:justify-end" data-testid="home-hero-image-wrap">
              <div className="bg-white rounded-3xl shadow-[0_4px_32px_rgba(0,0,0,0.06),0_1px_4px_rgba(0,0,0,0.04)] border border-gray-100/60 p-5 sm:p-7 max-w-lg">
                <img
                  src="/images/moxey-meet.png"
                  alt="Moxey AI Business Leader"
                  className="rounded-2xl object-contain w-full"
                  data-testid="home-hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ 2. HEY MOXEY ━━━ */}
      <section className="py-20 lg:py-28 px-6 bg-white" data-testid="home-hey-moxey">
        <div className="max-w-[900px] mx-auto text-center">
          <SectionLabel>Conversational AI</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="home-hey-title">
            Just say, <span className="font-script text-[#2563eb]">"Hey Moxey..."</span>
          </h2>
          <p className="text-gray-500 mt-3 text-base sm:text-lg leading-relaxed font-body max-w-lg mx-auto">
            You don't need to remember tools, workflows, or specialist names. Just tell Moxey what you need.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12" data-testid="home-prompt-grid">
            {examplePrompts.map((prompt, i) => (
              <div
                key={i}
                className="bg-[#f8f9fb] rounded-2xl px-6 py-5 border border-gray-100/80 flex items-center gap-3 text-left hover:border-[#2563eb]/30 transition-colors duration-300"
                data-testid={`home-prompt-${i}`}
              >
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <MessageSquareText className="w-4 h-4 text-[#2563eb]" />
                </div>
                <p className="text-gray-700 text-sm font-medium font-body leading-snug">{prompt}</p>
              </div>
            ))}
          </div>

          <p className="text-gray-400 text-sm font-body mt-8">
            Moxey understands the request, coordinates the right systems, and delivers results.
          </p>
        </div>
      </section>

      {/* ━━━ 3. WHAT MOXEY MANAGES ━━━ */}
      <section className="py-20 lg:py-28 px-6 bg-[#f8f9fb]" data-testid="home-manages">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Capabilities</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="home-manages-title">
              What Moxey Manages
            </h2>
            <p className="text-gray-500 mt-3 text-base max-w-lg mx-auto font-body leading-relaxed">
              Moxey works across the core parts of your business from one intelligent interface.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-manages-grid">
            {managesItems.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(37,99,235,0.07)] transition-shadow duration-300 group"
                data-testid={`home-manages-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-[#2563eb] transition-colors duration-300">
                  <item.icon className="w-5 h-5 text-[#2563eb] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-1.5 font-body">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ 4. HOW MOXEY WORKS ━━━ */}
      <section className="py-20 lg:py-28 px-6 bg-white" data-testid="home-how">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Process</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="home-how-title">
              How Moxey Works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-how-grid">
            {howSteps.map((step, i) => (
              <div
                key={i}
                className="bg-[#f8f9fb] rounded-2xl p-8 border border-gray-100/80 text-center relative"
                data-testid={`home-step-${i}`}
              >
                <span className="text-[#2563eb]/10 font-bold text-6xl absolute top-4 right-6 font-body select-none">{step.num}</span>
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
                  <step.icon className="w-7 h-7 text-[#2563eb]" />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2 font-body">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ 5. MODULES + INTEGRATIONS ━━━ */}
      <section className="py-20 lg:py-28 px-6 bg-[#f8f9fb]" data-testid="home-modules">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Platform</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="home-modules-title">
              Powered by the systems behind Moxey
            </h2>
            <p className="text-gray-500 mt-3 text-base max-w-lg mx-auto font-body leading-relaxed">
              Moxey uses native modules and connects to your existing tools through a modular integration layer.
            </p>
          </div>

          {/* Native Modules */}
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider font-body mb-4">Native Modules</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-modules-grid">
            {modules.map((mod, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.03)] hover:shadow-[0_4px_24px_rgba(37,99,235,0.08)] transition-shadow duration-300 group"
                data-testid={`home-module-${i}`}
              >
                <div className={`bg-gradient-to-r ${mod.gradient} px-6 py-5 flex items-center gap-3`}>
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <mod.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white font-body">{mod.name}</h3>
                    <p className="text-white/75 text-xs font-body">{mod.subtitle}</p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-500 text-sm leading-relaxed font-body mb-5">{mod.description}</p>
                  <button className="text-[#2563eb] text-sm font-semibold font-body flex items-center gap-1 hover:gap-2 transition-all">
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Third-Party Integrations */}
          <div className="mt-12">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider font-body mb-4">Third-Party Integrations</p>
            <div className="bg-white rounded-2xl border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-6" data-testid="home-integrations">
              <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
                {thirdPartyIntegrations.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#f8f9fb] border border-gray-100/80"
                    data-testid={`home-integration-${i}`}
                  >
                    <item.icon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600 font-body">{item.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-xs font-body mt-4 flex items-center gap-1.5">
                <Plug className="w-3 h-3" />
                Moxey connects to more tools through its modular integration layer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ 6. WHY BUSINESSES USE MOXEY ━━━ */}
      <section className="py-20 lg:py-28 px-6 bg-white" data-testid="home-benefits">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Benefits</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="home-benefits-title">
              Why businesses use Moxey
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-benefits-grid">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="bg-[#f8f9fb] rounded-2xl p-7 border border-gray-100/80 hover:shadow-[0_4px_24px_rgba(37,99,235,0.07)] transition-shadow duration-300 group"
                data-testid={`home-benefit-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-[#2563eb] transition-colors duration-300">
                  <b.icon className="w-5 h-5 text-[#2563eb] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-1.5 font-body">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ 7. WHAT MAKES MOXEY DIFFERENT ━━━ */}
      <section className="py-20 lg:py-28 px-6 bg-[#f8f9fb]" data-testid="home-different">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Why Moxey</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="home-different-title">
              What makes Moxey different
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="home-different-grid">
            {differentiators.map((d, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8 border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(37,99,235,0.07)] transition-shadow duration-300 group"
                data-testid={`home-diff-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-5 group-hover:bg-[#2563eb] transition-colors duration-300">
                  <d.icon className="w-5 h-5 text-[#2563eb] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2 font-body">{d.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ 8. FINAL CTA ━━━ */}
      <section className="py-20 lg:py-28 px-6 bg-white" data-testid="home-final-cta">
        <div className="max-w-[640px] mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight mb-4" data-testid="home-final-title">
            Put Moxey to work
          </h2>
          <p className="text-gray-500 text-base sm:text-lg leading-relaxed font-body mb-10 max-w-md mx-auto">
            Bring sales, marketing, operations, and execution together — across your native tools and connected systems — with one AI Business Leader.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-testid="home-final-buttons">
            <Link to="/hire-moxey">
              <Button
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-2xl px-10 py-6 text-base font-bold shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all"
                data-testid="home-final-hire-btn"
              >
                Hire Moxey
                <img src="/images/moxey-avatar.png" alt="Moxey" className="ml-2 w-5 h-5 rounded-full object-cover" />
              </Button>
            </Link>
            <Link to="/moxey">
              <Button
                variant="outline"
                className="rounded-2xl px-10 py-6 text-base font-semibold text-gray-700 border-gray-200 hover:bg-gray-50 transition-colors"
                data-testid="home-final-learn-btn"
              >
                Learn More
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Floating Open Moxey button */}
      <Link to="/app" className="fixed bottom-6 right-6 z-40" data-testid="home-floating-moxey">
        <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-full h-14 px-6 shadow-xl shadow-blue-500/25 hover:shadow-2xl hover:shadow-blue-500/30 transition-all font-body text-sm font-semibold">
          <img src="/images/moxey-avatar.png" alt="Moxey" className="w-5 h-5 rounded-full object-cover mr-2" />
          Open Moxey
        </Button>
      </Link>

    </div>
  );
}
